import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { Section } from "@/components/bits";
import { label } from "@/lib/api";

export const VIVID = ["#2DD4BF", "#D4AF37", "#FB7185", "#A78BFA", "#38BDF8", "#F97316"];
const MODES = [["week", "By Week"], ["month", "By Month"], ["type", "By Matter Type"]];

const TipBox = ({ children }) => <div className="rounded-md px-3 py-2 text-xs shadow-2xl" style={{ background: "#0B132B", border: "1px solid #2E3B61" }}>{children}</div>;

function HoursTip({ active, payload, label: l, mode }) {
  if (!active || !payload?.length) return null;
  return <TipBox><p className="text-slate-400 mb-1.5">{mode === "type" ? label(l) : l}</p>{payload.map((p) => <p key={p.name} className="flex items-center gap-2 text-slate-100"><span className="w-2 h-2 rounded-full" style={{ background: p.color }} />{p.name}<span className="ml-auto font-mono pl-4">{p.value}h</span></p>)}</TipBox>;
}

export function BillableHoursChart({ d }) {
  const [mode, setMode] = useState("week");
  const data = mode === "week" ? d.chart : mode === "month" ? d.chart_months : d.chart_types.map((r) => ({ ...r, matter_type: r.matter_type }));
  const xKey = mode === "week" ? "week" : mode === "month" ? "month" : "matter_type";
  return (
    <Section title="Billable hours per lawyer" testId="billable-hours-chart" className="xl:col-span-8"
      actions={<div className="flex gap-1 p-1 rounded-full bg-[#0B132B] border border-[#2E3B61]" data-testid="hours-chart-mode">{MODES.map(([k, l]) => <button key={k} onClick={() => setMode(k)} data-testid={`hours-mode-${k}`} className={`text-xs px-3 py-1 rounded-full transition-colors ${mode === k ? "bg-[#2DD4BF] text-[#0B132B] font-semibold" : "text-slate-400 hover:text-white"}`}>{l}</button>)}</div>}>
      <div className="h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} barGap={6} barCategoryGap="22%" margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#1C2541" />
            <XAxis dataKey={xKey} stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => (mode === "type" ? label(v) : v)} />
            <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} unit="h" />
            <Tooltip cursor={{ fill: "rgba(45,212,191,0.06)" }} content={<HoursTip mode={mode} />} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} iconType="circle" />
            {d.lawyers.map((l, i) => <Bar key={l} dataKey={l} fill={VIVID[i % VIVID.length]} radius={[8, 8, 2, 2]} maxBarSize={44} animationDuration={900} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Section>
  );
}

export function MatterTypeDonut({ data }) {
  const total = data.reduce((s, r) => s + r.count, 0);
  return (
    <Section title="Open matters by type" testId="matter-type-donut" className="xl:col-span-4">
      <div className="h-[400px] relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="matter_type" innerRadius="62%" outerRadius="85%" paddingAngle={3} cornerRadius={6} stroke="none" animationDuration={900}>
              {data.map((r, i) => <Cell key={r.matter_type} fill={VIVID[i % VIVID.length]} />)}
            </Pie>
            <Tooltip content={({ active, payload }) => active && payload?.length ? <TipBox><p className="text-slate-100 flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: payload[0].payload.fill }} />{label(payload[0].name)}<span className="ml-auto font-mono pl-4">{payload[0].value} open</span></p></TipBox> : null} />
            <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span className="text-slate-300">{label(v)}</span>} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none -mt-8" data-testid="donut-total">
          <p className="font-serif text-5xl font-bold text-white"><CountUp value={total} /></p>
          <p className="eyebrow mt-1">open matters</p>
        </div>
      </div>
    </Section>
  );
}

export function CountUp({ value, format = (v) => Math.round(v), duration = 800 }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now();
    const tick = (t) => { const p = Math.min(1, (t - start) / duration); setV(value * (1 - Math.pow(1 - p, 3))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{format(v)}</>;
}
